import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNotificationContext } from '../contexts/NotificationContext';

// Tabla estilo Excel: seleccion de celdas y rangos (click, arrastrar, Shift+click,
// encabezados de fila/columna), copiar (Ctrl+C), pegar desde Excel (Ctrl+V),
// edicion con doble click y exportacion a CSV.
// data: string[][] (filas)   headers: string[]   onChange({ headers, rows }) -> solo si editable

const clave = (r, c) => `${r}-${c}`;

const TAB = 9;
const LF = 10;
const CR = 13;
const COMILLA = 34;

// Lee texto copiado desde Excel (TSV). Excel entrecomilla las celdas que traen saltos de
// linea, tabuladores o comillas ("" = una comilla), asi que no se puede partir por "\n" a ciegas.
export function parseTSV(texto) {
  const s = String(texto || '');
  const filas = [];
  let fila = [];
  let celda = '';
  let entreComillas = false;
  for (let i = 0; i < s.length; i += 1) {
    const ch = s.charCodeAt(i);
    if (entreComillas) {
      if (ch === COMILLA) {
        if (s.charCodeAt(i + 1) === COMILLA) { celda += '"'; i += 1; } else { entreComillas = false; }
      } else {
        celda += s[i];
      }
    } else if (ch === COMILLA && celda === '') {
      entreComillas = true;
    } else if (ch === TAB) {
      fila.push(celda);
      celda = '';
    } else if (ch === LF || ch === CR) {
      if (ch === CR && s.charCodeAt(i + 1) === LF) i += 1;
      fila.push(celda);
      filas.push(fila);
      fila = [];
      celda = '';
    } else {
      celda += s[i];
    }
  }
  if (celda !== '' || fila.length > 0) {
    fila.push(celda);
    filas.push(fila);
  }
  return filas;
}

// Normaliza un bloque a matriz rectangular
export function bloqueRectangular(bloque) {
  const cols = Math.max(...bloque.map((f) => f.length), 1);
  return bloque.map((f) => Array.from({ length: cols }, (_, i) => f[i] ?? ''));
}

const esNumero = (v) => v.trim() !== '' && !Number.isNaN(Number(v.replace(',', '.')));

// Heuristica: la primera fila parece cabecera si no tiene celdas vacias ni numeros
export function pareceCabecera(bloque) {
  if (bloque.length < 2) return false;
  const primera = bloque[0];
  return primera.every((c) => c.trim() !== '') && !primera.some(esNumero);
}

// Pregunta como interpretar lo pegado: cabecera si/no y repetir valores de celdas combinadas
export function OpcionesPegado({ bloque, onAplicar, onCancelar }) {
  const datos = bloqueRectangular(bloque);
  const [conCabecera, setConCabecera] = useState(pareceCabecera(datos));
  const hayVaciosPrimeraColumna = datos.slice(1).some((f, i) => f[0].trim() === '' && datos.slice(0, i + 1).some((g) => g[0].trim() !== ''));
  const [repetir, setRepetir] = useState(hayVaciosPrimeraColumna);

  const aplicar = () => {
    let filas = datos.map((f) => [...f]);
    if (repetir) {
      let ultimo = '';
      filas = filas.map((f, i) => {
        if (conCabecera && i === 0) return f;
        if (f[0].trim() !== '') ultimo = f[0];
        else f[0] = ultimo;
        return f;
      });
    }
    const cols = datos[0].length;
    if (conCabecera) onAplicar({ headers: filas[0], rows: filas.length > 1 ? filas.slice(1) : [Array(cols).fill('')] });
    else onAplicar({ headers: Array(cols).fill(''), rows: filas });
  };

  return (
    <div className="rounded-lg border border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 p-3 text-sm">
      <p className="font-medium text-blue-900 dark:text-blue-100">
        Pegaste {datos.length} fila{datos.length > 1 ? 's' : ''} × {datos[0].length} columna{datos[0].length > 1 ? 's' : ''}
      </p>
      <label className="mt-2 flex items-start gap-2 cursor-pointer text-blue-900 dark:text-blue-100">
        <input type="checkbox" checked={conCabecera} onChange={(e) => setConCabecera(e.target.checked)} className="mt-0.5" />
        <span>La primera fila es la cabecera (nombres de las columnas)</span>
      </label>
      {hayVaciosPrimeraColumna && (
        <label className="mt-1.5 flex items-start gap-2 cursor-pointer text-blue-900 dark:text-blue-100">
          <input type="checkbox" checked={repetir} onChange={(e) => setRepetir(e.target.checked)} className="mt-0.5" />
          <span>Repetir el valor de arriba en las celdas vacías de la primera columna (celdas combinadas de Excel)</span>
        </label>
      )}
      <div className="mt-2.5 flex gap-2">
        <button type="button" onClick={aplicar} className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700">Usar estos datos</button>
        <button type="button" onClick={onCancelar} className="px-3 py-1.5 rounded-lg border border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-100 text-xs font-medium hover:bg-blue-100 dark:hover:bg-blue-900/30">Cancelar</button>
      </div>
    </div>
  );
}

export default function ExcelTable({ data, headers, editable = false, onChange, titulo = 'Tabla', maxHeight = 'max-h-96' }) {
  const { alertSuccess } = useNotificationContext();
  const [seleccion, setSeleccion] = useState(null); // { r1, c1, r2, c2 }
  const [arrastrando, setArrastrando] = useState(false);
  const [editando, setEditando] = useState(null); // { r, c } ; r === -1 -> encabezado
  const [valorEdicion, setValorEdicion] = useState('');
  const [pegadoPendiente, setPegadoPendiente] = useState(null); // bloque pegado esperando que se elija cabecera, etc.
  const contenedor = useRef(null);
  const inputRef = useRef(null);

  const cols = Math.max(headers?.length || 0, ...(data || []).map((f) => f.length), 0);
  const encabezados = Array.from({ length: cols }, (_, i) => headers?.[i] ?? '');
  const filas = (data || []).map((f) => Array.from({ length: cols }, (_, i) => f[i] ?? ''));

  const emitir = (nuevosHeaders, nuevasFilas) => {
    if (onChange) onChange({ headers: nuevosHeaders, rows: nuevasFilas });
  };

  const rango = seleccion && {
    rMin: Math.min(seleccion.r1, seleccion.r2),
    rMax: Math.max(seleccion.r1, seleccion.r2),
    cMin: Math.min(seleccion.c1, seleccion.c2),
    cMax: Math.max(seleccion.c1, seleccion.c2),
  };
  const seleccionada = (r, c) => rango && r >= rango.rMin && r <= rango.rMax && c >= rango.cMin && c <= rango.cMax;
  const cantidadSeleccionada = rango ? (rango.rMax - rango.rMin + 1) * (rango.cMax - rango.cMin + 1) : 0;

  const iniciarSeleccion = (r, c, e) => {
    if (editando) return;
    if (e.shiftKey && seleccion) {
      setSeleccion({ ...seleccion, r2: r, c2: c });
      return;
    }
    setSeleccion({ r1: r, c1: c, r2: r, c2: c });
    setArrastrando(true);
  };

  const extenderSeleccion = (r, c) => {
    if (arrastrando) setSeleccion((s) => (s ? { ...s, r2: r, c2: c } : s));
  };

  useEffect(() => {
    const soltar = () => setArrastrando(false);
    document.addEventListener('mouseup', soltar);
    return () => document.removeEventListener('mouseup', soltar);
  }, []);

  const seleccionarFila = (r) => filas.length && setSeleccion({ r1: r, c1: 0, r2: r, c2: cols - 1 });
  const seleccionarColumna = (c) => filas.length && setSeleccion({ r1: 0, c1: c, r2: filas.length - 1, c2: c });
  const seleccionarTodo = () => filas.length && setSeleccion({ r1: 0, c1: 0, r2: filas.length - 1, c2: cols - 1 });

  const textoSeleccion = useCallback(() => {
    if (!rango) return '';
    const lineas = [];
    for (let r = rango.rMin; r <= rango.rMax; r += 1) {
      const celdas = [];
      for (let c = rango.cMin; c <= rango.cMax; c += 1) celdas.push(filas[r]?.[c] ?? '');
      lineas.push(celdas.join('\t'));
    }
    return lineas.join('\n');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seleccion, data]);

  const copiar = useCallback(async () => {
    const texto = textoSeleccion();
    if (!texto) return;
    try {
      await navigator.clipboard.writeText(texto);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = texto;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    alertSuccess(`${cantidadSeleccionada} celda${cantidadSeleccionada > 1 ? 's' : ''} copiada${cantidadSeleccionada > 1 ? 's' : ''}`);
  }, [textoSeleccion, cantidadSeleccionada, alertSuccess]);

  const exportarCSV = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lineas = [encabezados.map(esc).join(','), ...filas.map((f) => f.map(esc).join(','))];
    const blob = new Blob([String.fromCharCode(65279) + lineas.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tabla.csv';
    a.click();
    URL.revokeObjectURL(url);
    alertSuccess('Tabla exportada como CSV');
  };

  // Ctrl+C solo cuando el foco/seleccion esta en esta tabla
  useEffect(() => {
    const alTeclear = (e) => {
      if (editando || !seleccion) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c' && contenedor.current?.contains(document.activeElement || e.target)) {
        e.preventDefault();
        copiar();
      }
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [editando, seleccion, copiar]);

  // Pegar desde Excel: rellena desde la celda seleccionada y agranda la tabla si hace falta
  const alPegar = (e) => {
    if (!editable || editando) return;
    const texto = e.clipboardData?.getData('text');
    if (!texto || (!texto.includes('\t') && !texto.includes('\n'))) return;
    e.preventDefault();
    const bloque = parseTSV(texto);
    const tablaVacia = !encabezados.some((h) => h.trim()) && !filas.some((f) => f.some((c) => c.trim()));
    if (tablaVacia) {
      setPegadoPendiente(bloque);
      return;
    }
    const r0 = rango ? rango.rMin : 0;
    const c0 = rango ? rango.cMin : 0;
    const nuevasFilas = filas.map((f) => [...f]);
    let nuevosCols = cols;
    bloque.forEach((fila, i) => {
      const r = r0 + i;
      while (nuevasFilas.length <= r) nuevasFilas.push(Array(nuevosCols).fill(''));
      fila.forEach((valor, j) => {
        const c = c0 + j;
        if (c >= nuevosCols) {
          nuevosCols = c + 1;
          nuevasFilas.forEach((f) => { while (f.length < nuevosCols) f.push(''); });
        }
        nuevasFilas[r][c] = valor;
      });
    });
    const nuevosHeaders = Array.from({ length: nuevosCols }, (_, i) => encabezados[i] ?? '');
    emitir(nuevosHeaders, nuevasFilas);
  };

  const empezarEdicion = (r, c) => {
    if (!editable) return;
    setEditando({ r, c });
    setValorEdicion(r === -1 ? encabezados[c] : filas[r][c]);
  };

  const confirmarEdicion = () => {
    if (!editando) return;
    const { r, c } = editando;
    if (r === -1) {
      const nuevos = [...encabezados];
      nuevos[c] = valorEdicion;
      emitir(nuevos, filas);
    } else {
      const nuevas = filas.map((f) => [...f]);
      nuevas[r][c] = valorEdicion;
      emitir(encabezados, nuevas);
    }
    setEditando(null);
  };

  useEffect(() => {
    if (editando && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editando]);

  const agregarFila = () => emitir(encabezados, [...filas, Array(cols).fill('')]);
  const agregarColumna = () => emitir([...encabezados, ''], filas.map((f) => [...f, '']));
  const quitarFilasSeleccionadas = () => {
    if (!rango) return;
    const nuevas = filas.filter((_, i) => i < rango.rMin || i > rango.rMax);
    emitir(encabezados, nuevas.length ? nuevas : [Array(cols).fill('')]);
    setSeleccion(null);
  };

  const celdaBase = 'border border-gray-200 dark:border-gray-700 px-2 text-sm h-8 align-middle';

  return (
    <div ref={contenedor} tabIndex={0} onPaste={alPegar} className="w-full outline-none">
      <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{titulo}</h4>
        <div className="flex flex-wrap gap-2">
          {editable && (
            <>
              <button type="button" onClick={agregarFila} className="px-2.5 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">+ Fila</button>
              <button type="button" onClick={agregarColumna} className="px-2.5 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">+ Columna</button>
              {rango && (
                <button type="button" onClick={quitarFilasSeleccionadas} className="px-2.5 py-1 text-xs rounded border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">Quitar filas</button>
              )}
            </>
          )}
          <button type="button" onClick={copiar} disabled={!rango} className="px-2.5 py-1 text-xs rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40">Copiar (Ctrl+C)</button>
          <button type="button" onClick={exportarCSV} className="px-2.5 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700">Exportar CSV</button>
        </div>
      </div>

      {pegadoPendiente && (
        <div className="mb-2">
          <OpcionesPegado
            bloque={pegadoPendiente}
            onAplicar={({ headers: h, rows }) => { emitir(h, rows); setPegadoPendiente(null); setSeleccion(null); }}
            onCancelar={() => setPegadoPendiente(null)}
          />
        </div>
      )}

      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
        <div className={`overflow-auto ${maxHeight} ${arrastrando ? 'select-none' : ''}`}>
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-900/50">
                <th onClick={seleccionarTodo} className={`${celdaBase} w-12 text-xs font-medium text-gray-500 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700`}>#</th>
                {encabezados.map((h, c) => (
                  <th
                    key={c}
                    onClick={() => seleccionarColumna(c)}
                    onDoubleClick={() => empezarEdicion(-1, c)}
                    className={`${celdaBase} min-w-24 text-xs font-semibold text-gray-600 dark:text-gray-300 text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700`}
                  >
                    {editando && editando.r === -1 && editando.c === c ? (
                      <input
                        ref={inputRef}
                        value={valorEdicion}
                        onChange={(e) => setValorEdicion(e.target.value)}
                        onBlur={confirmarEdicion}
                        onKeyDown={(e) => { if (e.key === 'Enter') confirmarEdicion(); if (e.key === 'Escape') setEditando(null); }}
                        className="w-full h-full bg-transparent outline-none"
                      />
                    ) : (h || <span className="text-gray-300 dark:text-gray-600 font-normal">{editable ? `Col ${c + 1}` : ''}</span>)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filas.map((fila, r) => (
                <tr key={r}>
                  <td onClick={() => seleccionarFila(r)} className={`${celdaBase} w-12 bg-gray-50 dark:bg-gray-900/50 text-xs font-medium text-gray-500 text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700`}>{r + 1}</td>
                  {fila.map((celda, c) => {
                    const activa = seleccionada(r, c);
                    return (
                      <td
                        key={c}
                        onMouseDown={(e) => iniciarSeleccion(r, c, e)}
                        onMouseEnter={() => extenderSeleccion(r, c)}
                        onDoubleClick={() => empezarEdicion(r, c)}
                        className={`${celdaBase} min-w-24 cursor-cell text-gray-900 dark:text-gray-100 ${activa ? 'bg-blue-50 dark:bg-blue-900/30 outline outline-1 outline-blue-400' : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'}`}
                      >
                        {editando && editando.r === r && editando.c === c ? (
                          <input
                            ref={inputRef}
                            value={valorEdicion}
                            onChange={(e) => setValorEdicion(e.target.value)}
                            onBlur={confirmarEdicion}
                            onMouseDown={(e) => e.stopPropagation()}
                            onKeyDown={(e) => { if (e.key === 'Enter') confirmarEdicion(); if (e.key === 'Escape') setEditando(null); }}
                            className="w-full h-full bg-transparent outline-none"
                          />
                        ) : (
                          <span className="block truncate max-w-xs">{celda}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
        {cantidadSeleccionada > 0 && <span>{cantidadSeleccionada} celda{cantidadSeleccionada > 1 ? 's' : ''} seleccionada{cantidadSeleccionada > 1 ? 's' : ''}</span>}
        {editable && <span className={cantidadSeleccionada > 0 ? 'ml-3' : ''}>Doble click para editar · Arrastra para seleccionar · Pega desde Excel con Ctrl+V</span>}
      </p>
    </div>
  );
}
